import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import jwt_decode from 'jwt-decode';
import { TrackJS } from 'trackjs';

import { authentication } from '@microsoft/teams-js';

import { microsoftRequest } from '@shared/services/EnvironmentVariables';
import { isInOutlook } from '@shared/utils/TeamsUtils';
import { AppState } from '@app/store/state/app.state';
import { redirectTo, setAuthType, setCalendarSyncActive, setUser, updateCurrentUser } from '@shared/store/auth.duck';
import { failureNotification } from '@shared/store/notification.duck';
import { updateOutlookSyncState } from '@features/settings/services/UserService';
import { rawUserRequest } from '@shared/services/Auth';

// Flag used to bridge the consent redirect: set before acquireTokenRedirect so
// that on the way back (LoginWithAzure mount) we can flip enableOutlookSync=true
// even though the in-memory state machine state is reset by the navigation.
export const CALENDAR_CONSENT_PENDING_KEY = 'calendar.consentPending';

// localStorage key used to persist popup-acquired session tokens across page
// reloads in hosts (Outlook) where the silent SSO path can't re-establish a
// session. Survives until expiry or until any logout/reset path clears it.
export const POPUP_SESSION_STORAGE_KEY = 'tt.popupSession';

function readStoredPopupSession(): SessionTokens | null {
  try {
    const raw = localStorage.getItem(POPUP_SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionTokens;
    if (!parsed.expiresOn || parsed.expiresOn < Date.now() + 60_000) {
      localStorage.removeItem(POPUP_SESSION_STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeStoredPopupSession(tokens: SessionTokens): void {
  try {
    localStorage.setItem(POPUP_SESSION_STORAGE_KEY, JSON.stringify(tokens));
  } catch {
    // Quota / private-mode failures are non-fatal — the in-memory cache still
    // works for the current session.
  }
}

import { GraphCooldownError, isConsentRequiredErr } from './AcquireTokenGateway';
import { DialogIdentifiers, openDialogWithPayload } from '@shared/store/dialog.duck';
import { getActiveGateway } from './AuthGatewayProvider';
import { resetAllLoginStorages } from '@shared/utils/AuthUtils';
import {
  AuthError,
  AuthLifecycle,
  AuthMachineState,
  AuthType,
  CalendarAccess,
  GraphTokens,
  SessionTokens,
} from './types';

const initialState: AuthMachineState = {
  lifecycle: 'unauthenticated',
  calendar: 'unknown',
  authType: null,
};

function enforceInvariants(state: AuthMachineState): void {
  if (
    state.lifecycle === 'unauthenticated' ||
    state.lifecycle === 'fatal' ||
    state.lifecycle === 'registering'
  ) {
    state.calendar = 'unknown';
  }
  if (state.calendar !== 'unknown' && state.lifecycle !== 'sessionReady') {
    if (process.env.NODE_ENV !== 'production') {
      TrackJS?.track(
        `[authMachine] invariant violation: calendar=${state.calendar} lifecycle=${state.lifecycle}`,
      );
    }
    state.calendar = 'unknown';
  }
}

const slice = createSlice({
  name: 'authMachine',
  initialState,
  reducers: {
    setLifecycle(state, action: PayloadAction<AuthLifecycle>) {
      state.lifecycle = action.payload;
      enforceInvariants(state);
    },
    setCalendar(state, action: PayloadAction<CalendarAccess>) {
      state.calendar = action.payload;
      enforceInvariants(state);
    },
    setError(state, action: PayloadAction<AuthError | undefined>) {
      state.lastError = action.payload;
    },
    setIdentity(state, action: PayloadAction<{ oid?: string; teamsUserId?: string }>) {
      state.oid = action.payload.oid ?? state.oid;
      state.teamsUserId = action.payload.teamsUserId ?? state.teamsUserId;
    },
    markConsentPrompted(
      state,
      action: PayloadAction<{ scopeKey: string; at: number }>,
    ) {
      state.lastConsentPromptAt = action.payload.at;
      state.lastConsentPromptScopeKey = action.payload.scopeKey;
    },
    reset() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    // When the legacy auth slice resets (logout, redirect, etc.),
    // mirror that into the state machine so phase doesn't drift.
    // Side-effect (gateway.invalidate) is handled in resetAllLoginStorages.
    builder.addCase('auth/reset', () => ({ ...initialState }));
  },
});

export const { setLifecycle } = slice.actions;
const { setCalendar, setError, setIdentity, reset: resetAuthMachine } = slice.actions;

export default slice.reducer;

// ---- selectors -------------------------------------------------------------

export const selectLifecycle = (state: AppState): AuthLifecycle =>
  state.authMachine.lifecycle;

export const selectCalendarAccess = (state: AppState): CalendarAccess =>
  state.authMachine.calendar;

const selectIsCalendarReady = (state: AppState): boolean =>
  state.authMachine.lifecycle === 'sessionReady' &&
  state.authMachine.calendar === 'ready';

const selectIsBootstrapping = (state: AppState): boolean =>
  state.authMachine.lifecycle === 'bootstrapping';

const selectIsRegistering = (state: AppState): boolean =>
  state.authMachine.lifecycle === 'registering';

const selectAuthError = (state: AppState): AuthError | undefined =>
  state.authMachine.lastError;

// ---- thunks ----------------------------------------------------------------

interface BootstrapAuthArgs {
  source: 'app-mount' | 'route-guard';
}

export const bootstrapAuth = createAsyncThunk<
  SessionTokens | null,
  BootstrapAuthArgs,
  { state: AppState }
>('authMachine/bootstrapAuth', async (args, thunkApi) => {
  const gateway = getActiveGateway();
  if (!gateway) return null;

  const current = thunkApi.getState().authMachine.lifecycle;
  if (current === 'bootstrapping' || current === 'sessionReady') {
    return null;
  }

  // In Outlook the host broker can't reliably satisfy silent SSO and the
  // gateway cache is in-memory only (empty after page reload). Use the
  // persisted popup tokens as a fallback so the user stays signed in across
  // reloads, but on app mount also attempt silent in case the broker is
  // healthy now (e.g. admin consent has since been granted) — that path gives
  // us fresher tokens than the persisted ones.
  if (isInOutlook()) {
    const stored = readStoredPopupSession();
    let tokens = null;

    if (args.source === 'app-mount') {
      thunkApi.dispatch(setLifecycle('bootstrapping'));
      thunkApi.dispatch(setError(undefined));
      try {
        tokens = await gateway.acquireSession({ source: 'bootstrap' });
        persistMicrosoftAuthType(thunkApi.dispatch);
        thunkApi.dispatch(setIdentity({ oid: tokens.oid }));
        thunkApi.dispatch(setLifecycle('sessionReady'));
        return tokens;
      } catch (silentErr: unknown) {
        TrackJS?.track(`[bootstrapAuth] silent failed in Outlook on app-mount, using cached tokens: ${String((silentErr as any)?.message ?? '').slice(0, 200)}`);
        // Fall through to seed from cache below.
      }
    }

    if (stored) {
      gateway.seedSession(stored);
      persistMicrosoftAuthType(thunkApi.dispatch);
      thunkApi.dispatch(setIdentity({ oid: stored.oid }));
      thunkApi.dispatch(setLifecycle('sessionReady'));

      // Validate the cached token against the backend by fetching the current
      // user. If it works, the cached session is still good — set the user and
      // navigate to the main page. If it fails (e.g. token revoked, account
      // deleted server-side), clear the stale cache and surface as auth failure
      // so the user is routed back to the login page.
      try {
        const user = await rawUserRequest();
        thunkApi.dispatch(setUser(user));
        thunkApi.dispatch(redirectTo('/whereabouts/team'));
      } catch (validateErr: unknown) {
        TrackJS?.track(`[bootstrapAuth] cached token validation failed: ${String((validateErr as any)?.message ?? '').slice(0, 200)}`);
        try { localStorage.removeItem(POPUP_SESSION_STORAGE_KEY); } catch {}
        gateway.invalidate({ kind: 'all' });
        thunkApi.dispatch(setLifecycle('unauthenticated'));
        throw validateErr;
      }

      return stored;
    }

    if (args.source === 'app-mount') {
      // No stored tokens AND we can't run silent here — bail so WithTeamsAuth's
      // catch clears auth-loading and routes the user to the login page.
      thunkApi.dispatch(setLifecycle('unauthenticated'));
      throw new Error('isInOutlook: no stored session, skipping silent bootstrap on app-mount');
    }
    // route-guard (user click) with no cached tokens — fall through;
    // loginWithMicrosoftPopup is the fallback for this case.
  }

  thunkApi.dispatch(setLifecycle('bootstrapping'));
  thunkApi.dispatch(setError(undefined));

  try {
    const tokens = await gateway.acquireSession({ source: 'bootstrap' });
    persistMicrosoftAuthType(thunkApi.dispatch);
    thunkApi.dispatch(setIdentity({ oid: tokens.oid }));
    thunkApi.dispatch(setLifecycle('sessionReady'));
    return tokens;
  } catch (err: unknown) {
    const error = toAuthError(err, 'unknown');
    thunkApi.dispatch(setError(error));
    thunkApi.dispatch(setLifecycle('unauthenticated'));
    throw err;
  }
});

interface RegisterArgs {
  source: 'web' | 'teams';
}

export const registerWithMicrosoft = createAsyncThunk<
  SessionTokens | null,
  RegisterArgs,
  { state: AppState }
>('authMachine/registerWithMicrosoft', async (_args, thunkApi) => {
  const gateway = getActiveGateway();
  if (!gateway) return null;

  thunkApi.dispatch(setLifecycle('bootstrapping'));
  thunkApi.dispatch(setError(undefined));

  try {
    const tokens = await gateway.acquireSession({ source: 'register' });
    persistMicrosoftAuthType(thunkApi.dispatch);
    thunkApi.dispatch(setIdentity({ oid: tokens.oid }));
    thunkApi.dispatch(setLifecycle('registering'));
    return tokens;
  } catch (err: unknown) {
    const error = toAuthError(err, 'unknown');
    thunkApi.dispatch(setError(error));
    thunkApi.dispatch(setLifecycle('unauthenticated'));
    throw err;
  }
});

export const loginWithMicrosoftPopup = createAsyncThunk<
  SessionTokens | null,
  void,
  { state: AppState }
>('authMachine/loginWithMicrosoftPopup', async (_args, thunkApi) => {
  const gateway = getActiveGateway();
  if (!gateway) return null;

  thunkApi.dispatch(setLifecycle('bootstrapping'));
  thunkApi.dispatch(setError(undefined));

  try {
    const scopes = microsoftRequest.login.scopes;
    const response = await authentication.authenticate({
      url: `${window.location.origin}/login/tab-auth?scopes=${encodeURIComponent(scopes.join(' '))}`,
      width: 600,
      height: 535,
      isExternal: true,
    } as any);
    const popupTokens = JSON.parse(response) as {
      idToken: string;
      accessToken: string;
      expiresIn?: string;
    };
    const claims = decodeJwtClaims(popupTokens.idToken) as { oid?: string; exp?: number };
    const expiresOn = popupTokens.expiresIn
      ? Date.now() + parseInt(popupTokens.expiresIn, 10) * 1000
      : claims.exp ? claims.exp * 1000 : Date.now() + 60 * 60 * 1000;
    const sessionTokens: SessionTokens = {
      idToken: popupTokens.idToken,
      accessToken: popupTokens.accessToken,
      expiresOn,
      oid: claims.oid ?? '',
    };
    gateway.seedSession(sessionTokens);
    writeStoredPopupSession(sessionTokens);
    persistMicrosoftAuthType(thunkApi.dispatch);
    thunkApi.dispatch(setIdentity({ oid: sessionTokens.oid }));
    thunkApi.dispatch(setLifecycle('sessionReady'));
    return sessionTokens;
  } catch (err: unknown) {
    const error = toAuthError(err, 'unknown');
    thunkApi.dispatch(setError(error));
    thunkApi.dispatch(setLifecycle('unauthenticated'));
    throw err;
  }
});

interface AcceptInviteArgs {
  inviteId: string;
  expectedEmail: string;
}

export const acceptInvite = createAsyncThunk<
  SessionTokens | null,
  AcceptInviteArgs,
  { state: AppState }
>('authMachine/acceptInvite', async (args, thunkApi) => {
  const gateway = getActiveGateway();
  if (!gateway) return null;

  thunkApi.dispatch(setLifecycle('bootstrapping'));
  thunkApi.dispatch(setError(undefined));

  try {
    const tokens = await gateway.acquireSession({ source: 'login' });

    const claims = decodeJwtClaims(tokens.idToken);
    const tokenEmail =
      ((claims.preferred_username as string | undefined) ??
        (claims.unique_name as string | undefined) ??
        (claims.email as string | undefined) ??
        '').toLowerCase();
    const expected = args.expectedEmail.toLowerCase();

    if (tokenEmail && expected && tokenEmail !== expected) {
      gateway.invalidate({ kind: 'all' });
      thunkApi.dispatch(
        setError({
          code: 'emailMismatch',
          message: `Invite is for ${expected} but signed in as ${tokenEmail}`,
        }),
      );
      thunkApi.dispatch(setLifecycle('unauthenticated'));
      return null;
    }

    persistMicrosoftAuthType(thunkApi.dispatch);
    thunkApi.dispatch(setIdentity({ oid: tokens.oid }));
    thunkApi.dispatch(setLifecycle('registering'));
    return tokens;
  } catch (err: unknown) {
    const error = toAuthError(err, 'unknown');
    thunkApi.dispatch(setError(error));
    thunkApi.dispatch(setLifecycle('unauthenticated'));
    throw err;
  }
});

interface RequestCalendarAccessArgs {
  source: 'auto' | 'manual';
  // Manual requests run silent-first: if Microsoft's consent UI would be
  // needed, we stop and open PermissionNeededDialog instead of escalating.
  // Only the dialog's Continue button re-dispatches with this flag set, which
  // permits the interactive popup/redirect (a real user gesture behind it).
  interactiveConfirmed?: boolean;
}

export const requestCalendarAccess = createAsyncThunk<
  GraphTokens | null,
  RequestCalendarAccessArgs,
  { state: AppState }
>('authMachine/requestCalendarAccess', async (args, thunkApi) => {
  const gateway = getActiveGateway();
  if (!gateway) {
    TrackJS?.track('[authMachine] requestCalendarAccess no-op: no active gateway');
    return null;
  }

  const state = thunkApi.getState().authMachine;
  if (state.calendar === 'pending') {
    // A request is already in flight; let the user wait for it. (Calendar can be
    // 'ready' too — we still allow re-acquire because the user explicitly clicked.)
    console.log('[authMachine] requestCalendarAccess calendar state pending');
    return null;
  }
  console.log('[authMachine] requestCalendarAccess', state.calendar);

  // Auto callers require lifecycle === 'sessionReady' (the bootstrap completed
  // and the user is properly authenticated through the state machine). Manual
  // callers are user gestures and should always attempt — the gateway will
  // surface a useful error if it can't proceed (e.g. no MSAL account). This
  // means a user who logged in via a legacy path (bootstrap never ran) can
  // still grant calendar consent by clicking the button.
  if (args.source === 'auto') {
    console.log('[authMachine] requestCalendarAccess source is auto');
    if (state.lifecycle !== 'sessionReady') {
      console.log('[authMachine] requestCalendarAccess not sessionReady', state.lifecycle);
      return null;
    }
    const cached = await gateway.tryGetGraphTokensSilently(microsoftRequest.calendarScopes);
    if (!cached) return null;
    thunkApi.dispatch(setCalendar('ready'));
    thunkApi.dispatch(setCalendarSyncActive(true));
    return cached;
  }
  console.log('[authMachine] requestCalendarAccess source is', args.source);

  thunkApi.dispatch(setCalendar('pending'));

  // Persist that the user has just confirmed consent. If acquireGraph escalates
  // to a redirect, control never returns from this thunk — the page reloads at
  // /login/azure. LoginWithAzure inspects this flag on mount and runs the same
  // post-success effect (enable outlook sync server-side, refetch user). Only
  // relevant for confirmed requests — the unconfirmed attempt is silent-only
  // and can never redirect.
  if (args.interactiveConfirmed) {
    try {
      localStorage.setItem(CALENDAR_CONSENT_PENDING_KEY, '1');
    } catch {
      // localStorage can throw in private mode / quota — non-fatal.
    }
  }

  try {
    const tokens = await gateway.acquireGraph({
      scopes: microsoftRequest.calendarScopes,
      source: args.source,
      // Escalating to Microsoft's consent UI (popup / redirect) is only allowed
      // once the user confirmed via PermissionNeededDialog. An unconfirmed
      // manual click attempts silently; consent-required failures open the
      // dialog in the catch below.
      interactive: args.interactiveConfirmed === true,
    });
    thunkApi.dispatch(setCalendar('ready'));
    thunkApi.dispatch(setCalendarSyncActive(true));

    // Silent acquire succeeded (no redirect). Persist the user's intent so the
    // backend reflects calendar sync as enabled and refetch the local user
    // state so other components (auto-step-up effect, profile UI) see it.
    if (args.source === 'manual') {
      try {
        await updateOutlookSyncState(true);
        await thunkApi.dispatch(updateCurrentUser(true));
      } catch (err) {
        TrackJS?.track(`[authMachine] failed to persist enableOutlookSync after manual success: ${err}`);
      }
      try { localStorage.removeItem(CALENDAR_CONSENT_PENDING_KEY); } catch {}
    }
    return tokens;
  } catch (err: unknown) {
    if (err instanceof GraphCooldownError) {
      // A successful acquire finished moments ago and this request landed inside
      // the cooldown window. Roll calendar back to its pre-request value —
      // leaving it at 'pending' would permanently block future requests via the
      // `calendar === 'pending'` guard above (nothing in-flight will clear it).
      thunkApi.dispatch(setCalendar(state.calendar));
      TrackJS?.track(`[authMachine] requestCalendarAccess cooldown for source=${args.source}`);
      return null;
    }

    // Silent attempt needs Microsoft's consent UI and the user hasn't confirmed
    // yet — ask first via our own dialog instead of opening the Microsoft
    // popup/redirect (which may show the "Need admin approval" page) unprompted.
    if (!args.interactiveConfirmed && isConsentRequiredErr(err)) {
      thunkApi.dispatch(setCalendar('consentRequired'));
      thunkApi.dispatch(setCalendarSyncActive(false));
      thunkApi.dispatch(openDialogWithPayload({
        activeDialog: DialogIdentifiers.PermissionNeededDialog,
        payload: { permissionRequired: 'MICROSOFT_CALENDAR' },
      }));
      TrackJS?.track('[authMachine] calendar consent required — showing confirmation dialog before Microsoft consent UI');
      return null;
    }
    const error = toAuthError(err, 'unknown');
    if (error.code === 'consentRequired') {
      thunkApi.dispatch(setCalendar('consentRequired'));
    } else {
      thunkApi.dispatch(setCalendar('disabled'));
    }
    thunkApi.dispatch(setCalendarSyncActive(false));
    thunkApi.dispatch(setError(error));

    // Surface failures to the user when they explicitly clicked. The auto path
    // stays silent so background failures don't toast.
    if (args.source === 'manual' && error.code !== 'cancelledByUser') {
      const message = error.code === 'popupClosed'
        ? 'Sign-in popup was closed. Try again — and check your browser allows popups for this site.'
        : `Failed to enable calendar sync: ${error.message}`;
      thunkApi.dispatch(failureNotification(message));
    }
    TrackJS?.track(`[authMachine] requestCalendarAccess failed: code=${error.code}, message=${error.message}`);
    return null;
  }
});

const clearAuth = createAsyncThunk<void, void, { state: AppState }>(
  'authMachine/clearAuth',
  async (_, thunkApi) => {
    const gateway = getActiveGateway();
    gateway?.invalidate({ kind: 'all' });
    thunkApi.dispatch(resetAuthMachine());
  },
);

// ---- helpers ---------------------------------------------------------------

function persistMicrosoftAuthType(dispatch: (action: unknown) => void): void {
  // Match the legacy `persistAuthType(AuthType.Microsoft, dispatch)` side-effects:
  // localStorage for the v4 fallback path, Redux state for the v5 + state-machine path.
  // Without this, tokenService.getAuthHeaders() can't tell the request is MS-authed
  // and falls through to default headers, breaking /register/azure and any other endpoint
  // that reads `accesstoken` from the header.
  try {
    localStorage.setItem('type', AuthType.Microsoft);
  } catch {
    // ignore
  }
  dispatch(setAuthType(AuthType.Microsoft));
}

function decodeJwtClaims(token: string): Record<string, unknown> {
  try {
    return jwt_decode(token) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function toAuthError(err: unknown, fallbackCode: AuthError['code']): AuthError {
  if (err && typeof err === 'object') {
    const e = err as { errorCode?: string; message?: string; correlationId?: string };
    if (e.errorCode === 'consent_required' || e.message?.includes('AADSTS65001') || e.message?.includes('Consent required')) {
      return { code: 'consentRequired', message: e.message ?? 'Consent required', correlationId: e.correlationId };
    }
    if (e.errorCode === 'user_cancelled' || e.message?.includes('CancelledByUser')) {
      return { code: 'cancelledByUser', message: e.message ?? 'User cancelled', correlationId: e.correlationId };
    }
    if (e.errorCode === 'popup_window_error' || e.message?.includes('popup_window_error')) {
      return { code: 'popupClosed', message: e.message ?? 'Popup closed', correlationId: e.correlationId };
    }
    if (e.message) {
      resetAllLoginStorages();
      return { code: fallbackCode, message: e.message, correlationId: e.correlationId };
    }
  }
  resetAllLoginStorages();
  return { code: fallbackCode, message: String(err) };
}
